import { CheckCircleOutlined } from "@ant-design/icons";
import type { AttentionItem, AttentionLevel, AttentionPage, WorkspaceCabinet } from "@wms/domain";
import { Tag } from "antd";
import { Link } from "react-router-dom";
import { formatDate, formatDateTime } from "../api/format";
import { objectLabel, targetPath } from "./attentionTarget";

export const LEVEL_TAG: Record<AttentionLevel, { color: string; word: string }> = {
  danger: { color: "error", word: "Срочно" },
  warning: { color: "warning", word: "Важно" },
  info: { color: "processing", word: "К сведению" },
};

/** A term of a row: a date of a batch or an invoice, or a moment of a slot or a milestone. */
export function dueText(due: string | null): string {
  if (!due) return "";
  return due.length <= 10 ? formatDate(due) : formatDateTime(due);
}

/**
 * UXW-27 / CW-20: one basis — its kind and number (or name), what happened, the warehouse, the state, the term and the
 * next step. The number and «Открыть» open exactly this record; the move itself confirms or posts nothing.
 */
export function AttentionRow({ cabinet, item }: { cabinet: WorkspaceCabinet; item: AttentionItem }) {
  const path = targetPath(cabinet, item.target);
  const label = objectLabel(item);
  const basisPath = item.basis ? targetPath(cabinet, item.basis) : null;
  return (
    <div className="wms-attention-row" data-testid={`attention-item-${item.id}`}>
      <div className="wms-attention-main">
        <span className="wms-attention-type">{item.object.type}</span>
        {path ? (
          <Link to={path} className="wms-attention-number" data-testid={`attention-link-${item.id}`}>
            {label}
          </Link>
        ) : (
          <span className="wms-attention-number">{label}</span>
        )}
        <span className="wms-attention-reason">{item.reason}</span>
      </div>
      <div className="wms-attention-meta">
        {item.basis && (
          <span>
            Основание: {basisPath ? <Link to={basisPath}>{item.basis.number}</Link> : item.basis.number}
          </span>
        )}
        {item.warehouse && <span>Склад {item.warehouse.code}</span>}
        {item.status && <span>{item.status}</span>}
        {item.due && <span>Срок: {dueText(item.due)}</span>}
        {path && (
          <Link to={path} className="wms-attention-open" aria-label={`${item.next_action}: ${label}`} data-testid={`attention-open-${item.id}`}>
            {item.next_action}
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * The groups of «Требует внимания» with their counts and first rows. A count never opens a random document of its
 * group: «Показать все N» leads to the list of exactly that group (UXW-28, CW-20).
 */
export function AttentionList({ cabinet, page, moreHref }: { cabinet: WorkspaceCabinet; page: AttentionPage; moreHref: (kind: string) => string | null }) {
  if (page.total === 0) {
    return (
      <div className="wms-attention-calm" data-testid="attention-calm">
        <CheckCircleOutlined />
        Всё спокойно: ничего не ждёт решения
      </div>
    );
  }
  return (
    <div className="wms-attention" data-testid="attention-list">
      {page.groups.map((group) => {
        const rows = page.items.filter((item) => item.kind === group.kind);
        const more = group.count > rows.length ? moreHref(group.kind) : null;
        return (
          <div key={group.kind} className="wms-attention-group" data-testid={`attention-group-${group.kind}`}>
            <div className="wms-attention-group-head">
              <Tag color={LEVEL_TAG[group.level].color}>{LEVEL_TAG[group.level].word}</Tag>
              <span className="wms-attention-group-label">{group.label}</span>
              <span className="wms-attention-count" data-testid={`attention-count-${group.kind}`}>
                {group.count}
              </span>
            </div>
            {rows.map((item) => (
              <AttentionRow key={item.id} cabinet={cabinet} item={item} />
            ))}
            {more && (
              <Link to={more} className="wms-attention-more" data-testid={`attention-more-${group.kind}`}>
                Показать все {group.count}
              </Link>
            )}
          </div>
        );
      })}
    </div>
  );
}
